import { type FormEvent, useState } from 'react';
import { i18n } from '@wms/shared';
import { ApiError, api } from '../lib/api.js';
import { Dialog } from './Dialog.js';
import { SkuCodeInput, SkuNameInput } from './SkuCodeInput.js';

const { t } = i18n;

export function CreateSkuDialog({
  partnerId,
  onClose,
  onCreated,
}: {
  partnerId: string;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [code, setCode] = useState('');
  const [type, setType] = useState<'PRODUCT' | 'COMPONENT'>('COMPONENT');
  const [nameLt, setNameLt] = useState('');
  const [nameEn, setNameEn] = useState('');
  const [notes, setNotes] = useState('');
  const [locationRow, setLocationRow] = useState('');
  const [locationShelf, setLocationShelf] = useState('');
  const [locationPosition, setLocationPosition] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await api(`/partners/${partnerId}/skus`, {
        method: 'POST',
        body: {
          code,
          type,
          nameLt,
          nameEn: nameEn || undefined,
          notes: notes.trim() || undefined,
          locationRow: locationRow.trim() || undefined,
          locationShelf: locationShelf.trim() || undefined,
          locationPosition: locationPosition.trim() || undefined,
        },
      });
      onCreated();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'sku_code_taken') {
        setError(t('sku.create.code_taken'));
      } else {
        setError(t('common.error'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog title={t('sku.create.title')} onClose={onClose}>
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-3 text-sm">
        <label className="block space-y-1">
          <span className="font-medium">{t('sku.col.code')}</span>
          <SkuCodeInput
            partnerId={partnerId}
            value={code}
            onChange={setCode}
            required
            className="w-full rounded border border-border bg-background px-2 py-1.5 font-mono"
          />
        </label>
        <label className="block space-y-1">
          <span className="font-medium">{t('sku.col.type')}</span>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as 'PRODUCT' | 'COMPONENT')}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          >
            <option value="COMPONENT">{t('sku.type.COMPONENT')}</option>
            <option value="PRODUCT">{t('sku.type.PRODUCT')}</option>
          </select>
        </label>
        <label className="block space-y-1">
          <span className="font-medium">{t('sku.col.name_lt')}</span>
          <SkuNameInput
            partnerId={partnerId}
            value={nameLt}
            onChange={setNameLt}
            required
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>
        <label className="block space-y-1">
          <span className="font-medium">{t('sku.col.name_en')}</span>
          <input
            value={nameEn}
            onChange={(e) => setNameEn(e.target.value)}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>
        <label className="block space-y-1">
          <span className="font-medium">{t('sku.edit.notes')}</span>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            maxLength={2000}
            rows={4}
            placeholder={t('sku.description.placeholder')}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>
        <div className="space-y-1">
          <span className="font-medium">{t('location.label')}</span>
          <div className="grid grid-cols-3 gap-2">
            <input
              value={locationRow}
              onChange={(e) => setLocationRow(e.target.value)}
              maxLength={8}
              placeholder={t('location.row')}
              className="rounded border border-border bg-background px-2 py-1.5 text-center font-mono"
            />
            <input
              value={locationShelf}
              onChange={(e) => setLocationShelf(e.target.value)}
              maxLength={8}
              placeholder={t('location.shelf')}
              className="rounded border border-border bg-background px-2 py-1.5 text-center font-mono"
            />
            <input
              value={locationPosition}
              onChange={(e) => setLocationPosition(e.target.value)}
              maxLength={8}
              placeholder={t('location.position')}
              className="rounded border border-border bg-background px-2 py-1.5 text-center font-mono"
            />
          </div>
          <p className="text-xs text-muted-foreground">{t('location.hint')}</p>
        </div>
        {error ? <div className="text-red-600">{error}</div> : null}
        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border px-3 py-1.5"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy || !code || !nameLt}
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
          >
            {busy ? t('common.loading') : t('common.save')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
